import { getUserFiles, type UserFile } from "./userFiles";

type Listener = () => void;

let userScope: string | null = null;
let cachedFiles: UserFile[] | undefined;
let inFlightPromise: Promise<UserFile[]> | null = null;
let storeGeneration = 0;
let storeVersion = 0;
const listeners = new Set<Listener>();

function clearUserFiles(): void {
  cachedFiles = undefined;
  inFlightPromise = null;
  storeGeneration += 1;
  storeVersion += 1;
  listeners.forEach((listener) => listener());
}

export function setUserFilesUserScope(nextUserScope: string | null | undefined): void {
  const normalizedScope = nextUserScope?.trim() || null;
  if (userScope === normalizedScope) return;
  userScope = normalizedScope;
  clearUserFiles();
}

export function getCachedUserFiles(): UserFile[] | undefined {
  return cachedFiles;
}

export function getUserFilesStoreVersion(): number {
  return storeVersion;
}

export function publishUserFiles(files: UserFile[]): void {
  cachedFiles = files;
  storeVersion += 1;
  listeners.forEach((listener) => listener());
}

export function subscribeToUserFiles(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function resetUserFilesStore(): void {
  userScope = null;
  clearUserFiles();
}

export async function ensureUserFiles(): Promise<UserFile[]> {
  if (cachedFiles) {
    return cachedFiles;
  }

  if (inFlightPromise) {
    return inFlightPromise;
  }

  const requestGeneration = storeGeneration;
  const requestPromise = getUserFiles()
    .then((files) => {
      if (requestGeneration === storeGeneration) {
        publishUserFiles(files);
      }
      return files;
    })
    .catch(() => cachedFiles ?? [])
    .finally(() => {
      if (inFlightPromise === requestPromise) {
        inFlightPromise = null;
      }
    });

  inFlightPromise = requestPromise;
  return requestPromise;
}

/** Changes only on reset or account switch, not on library refresh. */
export function getUserFilesScopeGeneration(): number {
  return storeGeneration;
}
